import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, User, Menu, X, Crown, Package, Award, Settings, LogOut, Bot, Gift, Heart, MapPin, LifeBuoy } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import PushNotificationToggle from "./PushNotificationToggle";
import InstallAppButton from "./InstallAppButton";

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();

  const cartCount = cart?.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <nav className="glass-nav fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_3ae66389-b093-4330-9743-6c6a00dc5113/artifacts/2iyfs3ge_ChatGPT%20Image%20Dec%2022%2C%202025%2C%2012_56_32%20PM%20-%20Copy.png"
              alt="Cut Above Polish Shop"
              className="h-14 w-auto"
            />
            <span className="font-heading text-xl tracking-wider hidden sm:block">CUT ABOVE</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/products" className="text-gray-300 hover:text-white transition-colors uppercase tracking-wide text-sm" data-testid="nav-products">Products</Link>
            <Link to="/categories" className="text-gray-300 hover:text-white transition-colors uppercase tracking-wide text-sm" data-testid="nav-categories">Categories</Link>
            <Link to="/vip" className="gold-text font-semibold uppercase tracking-wide text-sm flex items-center gap-1" data-testid="nav-vip">
              <Crown className="w-4 h-4 text-[#D4AF37]" />VIP
            </Link>
            <Link to="/university" className="text-gray-300 hover:text-white transition-colors uppercase tracking-wide text-sm" data-testid="nav-university">University</Link>
            <Link to="/affiliates" className="text-gray-300 hover:text-white transition-colors uppercase tracking-wide text-sm" data-testid="nav-affiliates">Affiliates</Link>
            <Link to="/wholesale" className="text-gray-300 hover:text-white transition-colors uppercase tracking-wide text-sm" data-testid="nav-wholesale">Wholesale</Link>
            <Link to="/chat" className="text-[#D62828] hover:text-white transition-colors uppercase tracking-wide text-sm flex items-center gap-1" data-testid="nav-chat">
              <Bot className="w-4 h-4" />Chat
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <div className="hidden md:inline-flex">
              <InstallAppButton variant="primary" label="Get the App" />
            </div>
            {user && (
              <PushNotificationToggle compact data-testid="nav-push-toggle" />
            )}
            <Link to="/cart" className="relative p-2 text-gray-300 hover:text-white transition-colors" data-testid="nav-cart">
              <ShoppingCart className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#D62828] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">{cartCount}</span>
              )}
            </Link>

            {user ? (
              <div className="relative group">
                <button className="flex items-center gap-2 p-2 text-gray-300 hover:text-white transition-colors" data-testid="nav-user-menu">
                  <User className="w-6 h-6" />
                  <span className="hidden sm:block text-sm">{user.name?.split(' ')[0]}</span>
                  {user.is_vip && <Crown className="w-4 h-4 text-[#D4AF37]" />}
                </button>
                <div className="absolute right-0 top-full mt-2 w-48 bg-[#121212] border border-white/10 rounded-sm shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <Link to="/account" className="flex items-center gap-2 px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 transition-colors" data-testid="nav-account">
                    <Settings className="w-4 h-4" />Account
                  </Link>
                  <Link to="/orders" className="flex items-center gap-2 px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 transition-colors" data-testid="nav-orders">
                    <Package className="w-4 h-4" />Orders
                  </Link>
                  <Link to="/wishlist" className="flex items-center gap-2 px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 transition-colors" data-testid="nav-wishlist">
                    <Heart className="w-4 h-4" />Wishlist
                  </Link>
                  <Link to="/addresses" className="flex items-center gap-2 px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 transition-colors" data-testid="nav-addresses">
                    <MapPin className="w-4 h-4" />Address Book
                  </Link>
                  <Link to="/support" className="flex items-center gap-2 px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 transition-colors" data-testid="nav-support">
                    <LifeBuoy className="w-4 h-4" />Support
                  </Link>
                  <Link to="/referrals" className="flex items-center gap-2 px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 transition-colors" data-testid="nav-referrals">
                    <Gift className="w-4 h-4 text-green-400" />Refer & Earn
                  </Link>
                  {user.is_admin && (
                    <Link to="/admin" className="flex items-center gap-2 px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 transition-colors" data-testid="nav-admin">
                      <Award className="w-4 h-4" />Admin
                    </Link>
                  )}
                  <button onClick={logout} className="flex items-center gap-2 w-full px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 transition-colors" data-testid="nav-logout">
                    <LogOut className="w-4 h-4" />Logout
                  </button>
                </div>
              </div>
            ) : (
              <Link to="/login" className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-4 py-2 rounded-sm font-bold uppercase tracking-wider text-sm transition-colors btn-glow" data-testid="nav-login">
                Login
              </Link>
            )}

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-gray-300" data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10">
            <div className="flex flex-col gap-4">
              <Link to="/products" onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-white uppercase tracking-wide text-sm py-2">Products</Link>
              <Link to="/categories" onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-white uppercase tracking-wide text-sm py-2">Categories</Link>
              <Link to="/vip" onClick={() => setMobileMenuOpen(false)} className="gold-text font-semibold uppercase tracking-wide text-sm py-2 flex items-center gap-2">
                <Crown className="w-4 h-4 text-[#D4AF37]" />VIP Membership
              </Link>
              <Link to="/university" onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-white uppercase tracking-wide text-sm py-2">Polishing University</Link>
              <Link to="/affiliates" onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-white uppercase tracking-wide text-sm py-2">Affiliates</Link>
              <Link to="/wholesale" onClick={() => setMobileMenuOpen(false)} className="text-gray-300 hover:text-white uppercase tracking-wide text-sm py-2">Wholesale / B2B</Link>
              <Link to="/chat" onClick={() => setMobileMenuOpen(false)} className="text-[#D62828] uppercase tracking-wide text-sm py-2 flex items-center gap-2">
                <Bot className="w-4 h-4" />AI Assistant
              </Link>
              <div className="pt-3 border-t border-white/5" onClick={() => setMobileMenuOpen(false)}>
                <InstallAppButton variant="primary" label="Get the App" />
              </div>
              {user && (
                <div className="py-2 border-t border-white/5">
                  <PushNotificationToggle compact />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
